import { Button, Col, Radio, RedoOutlined, Row, Switch } from 'gokwik-ui-kit'
import { Draggable } from '@library/components/draggable'
import PaymentMethod from './paymentMethod'
import { useEffect, useState } from 'react'
import { PaymentPageConfig, Config, ConfigDetailsInterface, ConfigCategory } from '@library/utilities/interface'
import ConfigDetails from './configDetails'
import styles from './config.module.css'
import { PAYMENT_METHODS } from '@library/utilities/constants/constants'
import { OtherControlsDetails } from './otherConfigs'
import { ConfigTypeEnum } from './constants'

interface Props {
    configData: PaymentPageConfig[]
    updateConfigValue: (config: Config, value: any) => void
    handleConfirm: () => void
    restoreValuesToDefault: () => void
    disableActions: boolean
    disableRevert: boolean
    platform: string
}

const paymentMethodCategories = ['method_ux_controls', 'payment_page']
const featureControls = ['onpl', 'cash_to_prepaid', 'twid']

const PaymentMethodsConfig = ({
    configData,
    handleConfirm,
    updateConfigValue,
    restoreValuesToDefault,
    disableActions,
    disableRevert,
    platform,
}: Props) => {
    const [customiseConfig, setCustomiseConfig] = useState<ConfigDetailsInterface>()
    const [otherControlsConfig, setOtherControlsConfig] = useState<boolean>(false)
    const [methodPositions, setMethodPositions] = useState<Config>()
    const [enabledMethods, setEnabledMethods] = useState<Config>()
    const [paymentPageConfig, setPaymentPageConfig] = useState<Config>()
    const [onplConfig, setOnplConfig] = useState<Config>()
    const [nativeMethodConfig, setNativeMethodConfig] = useState<Config[]>()
    const [twidConfig, setTwidConfig] = useState<Config>()
    const [c2pConfig, setC2pConfig] = useState<Config>()

    const setConfigs = (configData) => {
        let onplConfig
        let generalPaymentConfig
        let twidConfig
        let c2pConfig
        configData.forEach((config) => {
            if (config.name === 'onpl') {
                onplConfig = config.configs?.['general']?.find((config) => config.name === 'enable_onpl')
            } else if (config.name === 'payment_page') {
                generalPaymentConfig = config.configs?.['general']
            } else if (config.name === 'method_ux_controls') {
                const nativeMethodConfig = config.configs?.['native']
                setNativeMethodConfig(nativeMethodConfig)
            } else if (config.name === 'twid') {
                twidConfig = config.configs?.['general']?.find((config) => config.name === 'enable_twid')
            } else if (config.name === 'cash_to_prepaid') {
                c2pConfig = config.configs?.['general']?.find((config) => config.name === 'enable_cash_to_prepaid')
            }
        })
        setOnplConfig(onplConfig as Config)
        setTwidConfig(twidConfig as Config)
        setC2pConfig(c2pConfig as Config)
        if (!generalPaymentConfig) return
        const methodPositionsConfig = generalPaymentConfig?.find((config) => config.name === 'method_positions') || {}
        const enabledMethodConfig = generalPaymentConfig?.find((config) => config.name === 'enabled_methods') || {}
        const paymentPageConfig = generalPaymentConfig?.find((config) => config.name === 'page_style') || {}

        // Get all unique high-level methods from enabled_methods
        const enabledOptions = enabledMethodConfig?.validations?.options || []
        const instrumentCategoryMappings = {
            wallet: PAYMENT_METHODS.WALLET,
            bnpl: PAYMENT_METHODS.BNPL,
            cc: PAYMENT_METHODS.CARDS,
            dc: PAYMENT_METHODS.CARDS,
            emi: PAYMENT_METHODS.EMI,
        }

        const mappedEnabledMethods = new Set()
        enabledOptions.forEach((method) => {
            const [methodName] = method.split('-')
            const mapped = instrumentCategoryMappings[methodName] || methodName
            mappedEnabledMethods.add(mapped)
        })
        if (methodPositionsConfig) {
            // Use order from method_positions and preserve only those in enabled
            const positionOrder = methodPositionsConfig?.value || []
            const orderedMethods = positionOrder?.filter((method) => mappedEnabledMethods.has(method))

            // Append remaining enabled methods not found in method_positions
            const allOrderedMethods = [
                ...orderedMethods,
                ...[...mappedEnabledMethods].filter((method) => !positionOrder.includes(method)),
            ]
            methodPositionsConfig.options = allOrderedMethods
            setMethodPositions(methodPositionsConfig)
        }
        if (enabledMethodConfig) setEnabledMethods(enabledMethodConfig)
        if (paymentPageConfig) setPaymentPageConfig(paymentPageConfig)
    }

    useEffect(() => {
        if (configData) setConfigs(configData)
    }, [configData])

    const otherControls = configData.filter(
        (config) => !featureControls.includes(config.name) && !paymentMethodCategories.includes(config.name),
    )

    useEffect(() => {
        setCustomiseConfig({ config: {}, name: '' })
        setEnabledMethods(null)
        setPaymentPageConfig(null)
        setOtherControlsConfig(false)
    }, [platform])

    const handleCustomisation = ({ config, name }: ConfigDetailsInterface) => {
        const methodName = Object.keys(config)?.[0] ?? ''
        if (methodName === PAYMENT_METHODS.COD) {
            const ppdcodConfig = configData?.find((data) => data.name === 'method_ux_controls')?.configs?.['ppcod']
            if (ppdcodConfig) {
                setCustomiseConfig({
                    config: { ...config, ppcod: ppdcodConfig },
                    name: 'COD / Partial COD',
                })
                return
            }
        }
        if (name === ConfigTypeEnum.OTHER_CONTROLS) {
            setOtherControlsConfig(true)
        } else if (otherControls.some((control) => control.title === name)) {
            setOtherControlsConfig(false)
        }
        setCustomiseConfig({ config, name })
    }

    return (
        <div className='w-full h-full box-border relative'>
            {!!customiseConfig?.name && !otherControlsConfig ? (
                <ConfigDetails
                    enabledMethodsConfig={enabledMethods}
                    configInfo={customiseConfig}
                    resetCustomiseDetails={() => {
                        if (otherControls.some((control) => control.title === customiseConfig.name)) {
                            handleCustomisation({
                                config: otherControls.flatMap(
                                    (control) => control.configs ?? [],
                                ) as unknown as ConfigCategory,
                                name: ConfigTypeEnum.OTHER_CONTROLS,
                            })
                            setOtherControlsConfig(true)
                        } else {
                            handleCustomisation({ config: {}, name: '' })
                        }
                    }}
                    updateConfigDetails={updateConfigValue}
                    paymentPageConfig={paymentPageConfig}
                />
            ) : otherControlsConfig ? (
                <OtherControlsDetails
                    configInfo={customiseConfig}
                    resetCustomiseDetails={() => {
                        setOtherControlsConfig(false)
                        handleCustomisation({ config: {}, name: '' })
                    }}
                    handleCustomisation={handleCustomisation}
                />
            ) : (
                <>
                    {configData?.some((data) => featureControls.includes(data.name)) && (
                        <Row className='bg-white mt-4 p-4 rounded-md box-border flex flex-col gap-4'>
                            <Col span={24}>
                                <h4 className='mb-1 mt-0 text-base font-semibold'>{ConfigTypeEnum.FEATURE_CONTROLS}</h4>
                            </Col>
                            {configData
                                .filter((data) => featureControls.includes(data.name))
                                .map((data) => (
                                    <Col span={24} key={data.name} className='flex mt-2'>
                                        <div className='header w-[40%]'>
                                            <h4 className='m-0 text-sm font-medium'>{data.title}</h4>
                                            <p className='pt-1 text-gray-500 text-xs font-normal'>{data.description}</p>
                                        </div>
                                        <div className='value w-[60%] flex gap-3 justify-end'>
                                            {data.name === 'onpl' && (
                                                <Switch
                                                    size='small'
                                                    className='mt-2'
                                                    checked={onplConfig?.value as boolean}
                                                    onChange={(checked) => updateConfigValue(onplConfig, checked)}
                                                />
                                            )}
                                            {data.name === 'twid' && (
                                                <Switch
                                                    size='small'
                                                    className='mt-2'
                                                    checked={twidConfig?.value as boolean}
                                                    onChange={(checked) => updateConfigValue(twidConfig, checked)}
                                                />
                                            )}
                                            {data.name === 'cash_to_prepaid' && (
                                                <Switch
                                                    size='small'
                                                    className='mt-2'
                                                    checked={c2pConfig?.value as boolean}
                                                    onChange={(checked) => updateConfigValue(c2pConfig, checked)}
                                                />
                                            )}
                                            <Button
                                                variant='default'
                                                className='border-gray-400 text-xs p-2'
                                                onClick={() =>
                                                    handleCustomisation({ config: data.configs, name: data.title })
                                                }
                                            >
                                                Customise
                                            </Button>
                                        </div>
                                    </Col>
                                ))}
                        </Row>
                    )}
                    {otherControls.length > 0 && (
                        <Row className='bg-white mt-4 p-4 rounded-md box-border flex flex-col gap-4'>
                            <Col span={24} className='flex justify-between'>
                                <h4 className='mb-1 mt-0 text-base font-semibold'>{ConfigTypeEnum.OTHER_CONTROLS}</h4>
                                <Button
                                    variant='default'
                                    className='border-gray-400 text-xs p-2'
                                    onClick={() =>
                                        handleCustomisation({
                                            config: otherControls.flatMap(
                                                (control) => control.configs ?? [],
                                            ) as unknown as ConfigCategory,
                                            name: ConfigTypeEnum.OTHER_CONTROLS,
                                        })
                                    }
                                >
                                    Customise
                                </Button>
                            </Col>
                        </Row>
                    )}
                    {paymentPageConfig && (
                        <Row className='bg-white mt-4 p-4 rounded-md box-border flex flex-col gap-8'>
                            <Col span={24} className='flex justify-between'>
                                <h4 className='mb-1 mt-0 text-base font-semibold'>Method Controls</h4>
                                <Button
                                    variant='default'
                                    className='border-gray-400 text-xs p-2'
                                    onClick={() =>
                                        handleCustomisation({
                                            config:
                                                configData?.find((data) => data.name === 'payment_page')?.configs ?? {},
                                            name: 'Payment Page',
                                        })
                                    }
                                >
                                    Customise
                                </Button>
                            </Col>
                            <Col span={24} className='w-full flex justify-between flex-start mt-2'>
                                <div className='header w-[30%] mr-1'>
                                    <h4 className='m-0 text-sm font-medium'>Method UI Style</h4>
                                </div>
                                <div className='value w-[70%] flex justify-between pr-1'>
                                    <div className='value '>
                                        <Radio.Group
                                            onChange={(e) => updateConfigValue(paymentPageConfig, e.target.value)}
                                            value={paymentPageConfig?.value}
                                            options={[
                                                {
                                                    value: (paymentPageConfig?.validations as any)?.options[1],
                                                    label: (
                                                        <div className='m-2'>
                                                            <div className={styles.imgWrapper}>
                                                                <span
                                                                    className={`${styles.paymentsGlobalImg} ${styles.defaultStyle}`}
                                                                ></span>
                                                            </div>
                                                            <div>
                                                                <h4 className='m-0 p-0 pt-2 text-sm font-medium'>
                                                                    Default Style
                                                                </h4>
                                                                <p className='pt-1 text-gray-500 text-xs font-normal'>
                                                                    Customised button colors
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ),
                                                },
                                                {
                                                    value: (paymentPageConfig?.validations as any)?.options[0],
                                                    label: (
                                                        <div className='m-2'>
                                                            <div className={styles.imgWrapper}>
                                                                <span
                                                                    className={`${styles.paymentsGlobalImg} ${styles.listStyle}`}
                                                                ></span>
                                                            </div>
                                                            <div>
                                                                <h5 className='m-0 p-0 pt-2 text-sm font-medium'>
                                                                    List Style
                                                                </h5>
                                                                <p className='pt-1 text-gray-500 text-xs font-normal'>
                                                                    Only White button colors
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ),
                                                },
                                            ]}
                                        />
                                    </div>
                                </div>
                            </Col>
                            {configData.map((data) => {
                                return (
                                    data.name === 'method_ux_controls' && (
                                        <Col span={24} key={data.name}>
                                            <div className='w-full flex justify-between flex-start'>
                                                <div className='header  w-[40%] mr-2'>
                                                    <h4 className='m-0 text-sm font-medium'>Payment Methods</h4>
                                                    <p className='pt-1 text-gray-500 text-xs font-normal'>
                                                        Customise payment method visibility, sorting order as well as
                                                        button colors, text, and badges
                                                    </p>
                                                </div>
                                                <div className='value w-[60%] pr-1'>
                                                    <Draggable
                                                        onDragEnd={(items) => updateConfigValue(methodPositions, items)}
                                                    >
                                                        {(methodPositions?.options as Array<string>)?.map((method) => (
                                                            <PaymentMethod
                                                                key={method}
                                                                enabledMethods={enabledMethods}
                                                                configs={
                                                                    data.configs[
                                                                        method === PAYMENT_METHODS.SIMPLPAYIN3
                                                                            ? PAYMENT_METHODS.PAY_IN_X
                                                                            : method
                                                                    ]
                                                                }
                                                                handleCustomisation={handleCustomisation}
                                                                updateConfigValue={updateConfigValue}
                                                            />
                                                        ))}
                                                    </Draggable>
                                                </div>
                                            </div>
                                        </Col>
                                    )
                                )
                            })}
                            {nativeMethodConfig && nativeMethodConfig?.length > 0 && (
                                <Col span={24}>
                                    <div className='w-full flex justify-between flex-start'>
                                        <div className='header  w-[40%] mr-2'>
                                            <h4 className='m-0 text-sm font-medium'>
                                                {nativeMethodConfig[0]?.subcategoryTitle}
                                            </h4>
                                            <p className='pt-1 text-gray-500 text-xs font-normal'>
                                                {nativeMethodConfig[0]?.subcategoryDescription}
                                            </p>
                                        </div>
                                        <div className='value w-[60%] pr-1'>
                                            <PaymentMethod
                                                enabledMethods={enabledMethods}
                                                configs={nativeMethodConfig}
                                                handleCustomisation={handleCustomisation}
                                                updateConfigValue={updateConfigValue}
                                            />
                                        </div>
                                    </div>
                                </Col>
                            )}
                        </Row>
                    )}
                </>
            )}
            {!customiseConfig?.name && (
                <Row className='bg-white mt-4 mb-2 p-4 rounded-sm box-border sticky bottom-0 left-0 right-0 border-0 border-t border-gray-200 border-solid'>
                    <Col span={24} className='flex gap-4 justify-end'>
                        <Button
                            variant='default'
                            onClick={restoreValuesToDefault}
                            disabled={disableActions || disableRevert}
                            className='w-[240px]'
                        >
                            <RedoOutlined /> Revert Changes
                        </Button>
                        <Button
                            variant='primary'
                            className='w-[240px]'
                            onClick={handleConfirm}
                            disabled={disableActions}
                        >
                            Confirm Changes
                        </Button>
                    </Col>
                </Row>
            )}
        </div>
    )
}
export default PaymentMethodsConfig
